import {useMemo } from "react"
import styled from "@emotion/styled"
import graphql from "babel-plugin-relay/macro"
import { useSubscription } from "react-relay"
import PopperContainer from "../Radiate/PopperContainer/PopperContainer"
import { CallingThread } from "./CallingThread"
import theme from "../../stylesheets/theme"
import { IncomingCallModal } from "./IncomingCallModal"
import { useCallContext } from "./CallContext"
import { BetaBadge } from "./CallingThreadStyle"
import { IncomingCallIcon } from "."

const CallingThreadsPopoverContainer = styled.div`
    width: fit-content;
    min-width: 300px;
    max-height: 450px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px;
    background-color: ${theme.colors.gray02};
    overflow: hidden;
    overflow-y: auto;
`

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 0;
    font-size: 14px;
    font-weight: 500;

    svg {
        color: #25D366;
        height: 18px;
        width: 18px;
        margin-right: 4px;
    }
`

const ThreadsWrapper = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const NoCallsMessage = styled.div`
    padding: 20px;
    text-align: center;
    color: ${theme.colors.gray6};
    font-size: 14px;
`

const CallSubscription = graphql`
  subscription CallingThreadsPopoverSubscription($wChannelIds: [ID]) {
    call(wChannelIds: $wChannelIds) {
      action
      call {
        id
        _id
        callId
        callInfo
        wAppId
        wChannelId
        wMemberId
        from
        to
        direction
        status
        member {
          externalId
          externalProfile 
        }
        timestamp
        createdAt
        updatedAt
        etag
      }
    }
  }
`

const CallingThreadsPopover = ({ isOpen, setIsOpen, wChannelIds }) => {
    const { incomingCalls, makeIncomingCallActive, makeCallAccepted, makeCallTerminated, startOutgoingCall, makeOutgoinCallRinging, handleCallActive } = useCallContext()

    const subscriptionConfig = useMemo(() => {
        return {
            variables: {
                wChannelIds: wChannelIds,
            },
            subscription: CallSubscription,
            onNext: async (response) => {
                const { action, call } = response.call || {}
                console.log("Call subscription received:", { action, call })
                
                if (!call) return

                const formattedCall = {
                    id: call?.id,
                    _id: call?._id,
                    callId: call?.callInfo?.callId || call?.callId,
                    wAppId: call?.wAppId,
                    wChannelId: call?.wChannelId, 
                    wMemberId: call?.wMemberId,
                    from: call?.from,
                    to: call?.to,
                    direction: call?.direction,
                    status: call?.status,
                    event: call?.callInfo?.event,
                    callInfo: call?.callInfo,
                    member: call?.member,
                    session: {
                        sdpType: call?.callInfo?.session?.sdpType,
                        sdp: call?.callInfo?.session?.sdp,
                    },
                    action: action,
                    timestamp: call?.timestamp,
                    createdAt: call?.createdAt,
                    updatedAt: call?.updatedAt,
                    etag: call?.etag,
                }

                console.log("Call subscription formated call:", { formattedCall })

                switch (action) {
                    case "ADD": {
                        // handle user initiated call
                        if (formattedCall.direction === "USER_INITIATED") {
                            makeIncomingCallActive({...formattedCall, callType: "INCOMING", callStatus: "RINGING"})  
                        }
                        // handle business initiated call
                        // if (formattedCall.direction === "BUSINESS_INITIATED") {
                        //     makeCallAccepted({...formattedCall, callType: "OUTGOING", callStatus: "INITIATING" })
                        // }
                        break
                    }
                    case "UPDATE": {
                        // handle user initiated call
                        if (formattedCall.direction === "USER_INITIATED") {
                            switch (formattedCall.status) {
                                case "ACCEPTED": {
                                    makeCallAccepted({...formattedCall, callType: "INCOMING", callStatus: "ACCEPTED"})
                                    break
                                }
                                case "TERMINATED": {
                                    makeCallTerminated({...formattedCall, callType: "INCOMING", callStatus: "TERMINATED"})
                                    break
                                }
                                case "COMPLETED": 
                                  makeCallTerminated({...formattedCall, callType: "INCOMING", callStatus: "TERMINATED"})
                                  break
                                case "active": 
                                  handleCallActive(formattedCall)
                                  break
                                default:
                                    console.log("Call subscription default case (user initiated):", { formattedCall })
                                    makeCallTerminated({...formattedCall, callType: "INCOMING", callStatus: "TERMINATED"})
                                    break
                            }
                        }
                        // handle business initiated call
                        if (formattedCall.direction === "BUSINESS_INITIATED") {
                            switch (formattedCall.status) {
                                case "RINGING": {
                                    makeOutgoinCallRinging({...formattedCall, callType: "OUTGOING" })
                                    break
                                }
                                case "ACCEPTED": {
                                    startOutgoingCall({...formattedCall, callType: "OUTGOING", callStatus: "ACCEPTED"})
                                    break;
                                }
                                case "TERMINATED": {
                                    makeCallTerminated({...formattedCall, callType: "OUTGOING", callStatus: "TERMINATED"})
                                    break
                                }
                                case "COMPLETED": {
                                    makeCallTerminated({...formattedCall, callType: "OUTGOING", callStatus: "TERMINATED"})
                                    break
                                }
                                default:
                                    console.log("Call subscription default case (business initiated):", { formattedCall })
                                    makeCallTerminated({...formattedCall, callType: "OUTGOING", callStatus: "TERMINATED"})
                                    break
                            }
                        }
                        break;
                    }
                    default: 
                        break
                }
            },
            onError: (error) => {
                console.error("Call subscription error:", error)
            }
        }
    }, [wChannelIds])

    useSubscription(subscriptionConfig)

    return (
        <>
            {/* Floating modal - only shows when there's an active call */}
            <IncomingCallModal onClose={() => setIsOpen(false)} />

          <PopperContainer 
            show={isOpen}
            disabled={false}
            placement="bottom-end"
            modifiers={[
            {
                name: "offset",
                options: {
                  offset: [8, 8],
                },
            },
            ]}
            onClickOutside={() => {setIsOpen(false)}}
          >
           {() => ( 
            <CallingThreadsPopoverContainer>
                <Header>
                    <IncomingCallIcon size={20} />
                    Incoming WhatsApp Call
                    <BetaBadge>BETA</BetaBadge>
                </Header>
                <ThreadsWrapper>
                    {incomingCalls.length > 0 ? (
                        incomingCalls.map((call) => (
                            <CallingThread 
                                key={call.callId}
                                call={call}
                                setIsOpen={setIsOpen}
                            />
                        ))
                    ) : (
                        <NoCallsMessage>No incoming calls</NoCallsMessage>
                    )}
                </ThreadsWrapper>
            </CallingThreadsPopoverContainer>
           )} 
          </PopperContainer>
        </>
    )
}

export default CallingThreadsPopover
